import React, { useRef, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { generateBiltyPDF, overlaySignatureOnBilty, BiltyData } from '../utils/biltyUtils';

// Brand colors
const PRIMARY_COLOR = '#3498db';
const PRIMARY_DARK = '#2980b9';
const ERROR_COLOR = '#e74c3c';
const SUCCESS_COLOR = '#27ae60';

interface OrderData {
  lrNumber: string;
  invoiceNo: string;
  noOfArticles: number;
  orderedAt: string;
  pickupCompanyName: string;
  deliveryCompanyName: string;
}

const DeliverySignature: React.FC = () => {
  const { token: tokenFromUrl } = useParams<{ token: string }>();
  const location = useLocation();
  const signaturePadRef = useRef<SignatureCanvas>(null);
  const biltyPrintRef = useRef<HTMLDivElement>(null);
  
  // Check if we're in demo mode
  const isDemoMode = location.pathname === '/delivery-signature/demo';
  
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [isEmpty, setIsEmpty] = useState<boolean>(true);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  
  // API states
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [token, setToken] = useState<string>('');
  const [capturedSignature, setCapturedSignature] = useState<string>('');
  const [downloadingBilty, setDownloadingBilty] = useState<boolean>(false);
  const [userIpAddress, setUserIpAddress] = useState<string>('');

  // Fetch user's IP address on mount
  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setUserIpAddress(data.ip);
      } catch (error) {
        console.error('Failed to fetch IP address:', error);
        setUserIpAddress('Unable to fetch IP');
      }
    };

    fetchIpAddress();
  }, []);

  // Get token from URL on mount
  useEffect(() => {
    if (isDemoMode) {
      // In demo mode, skip token verification and use dummy data
      setOrderData({
        lrNumber: 'DEMO123456',
        invoiceNo: 'INV-DEMO-2026',
        noOfArticles: 5,
        orderedAt: new Date().toISOString(),
        pickupCompanyName: 'Sender Company Pvt Ltd',
        deliveryCompanyName: 'Receiver Company Ltd',
      });
      setLoading(false);
      return;
    }
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      verifyToken(tokenFromUrl);
    } else {
      setError('No signature token provided in URL');
      setLoading(false);
    }
  }, [tokenFromUrl, isDemoMode]);

  // Auto-download demo bilty in demo mode after submission
  useEffect(() => {
    if (submitted && isDemoMode && capturedSignature && biltyPrintRef.current && !downloadingBilty) {
      // Automatically trigger download in demo mode
      handleDownloadDemoBilty();
    }
  }, [submitted, isDemoMode, capturedSignature]);

  // Verify token and get order data
  const verifyToken = async (signatureToken: string) => {
    setLoading(true);
    setError('');

    try {
      const base = (process.env.REACT_APP_BACKEND_API_ROUTE || '').replace(/\/$/, '');
      const baseTrimmed = base.replace(/\/$/, '');
      const url = `${baseTrimmed}/orders/verify-pod-token?token=${encodeURIComponent(signatureToken)}`;

      const res = await fetch(url, { method: 'GET' });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || `HTTP ${res.status}`);
      }

      const data = await res.json();

      if (!data.valid) {
        setError(data.message || 'Invalid or expired signature link');
        setLoading(false);
        return;
      }

      if (data.orderData) {
        setOrderData(data.orderData);
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Error verifying token:', err);
      setError(err.message || 'Failed to verify signature link');
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Demo bilty download handler
  const handleDownloadDemoBilty = async () => {
    if (!biltyPrintRef.current || !capturedSignature) {
      console.error('Unable to generate bilty: missing refs or signature');
      return;
    }

    setDownloadingBilty(true);

    try {
      // Create sample bilty data with dummy values (without signature)
      const sampleBiltyData: BiltyData = {
        lrNumber: orderData?.lrNumber || 'DEMO123456',
        bookingDate: orderData?.orderedAt ? formatDate(orderData.orderedAt) : new Date().toLocaleDateString('en-IN'),
        invoiceNo: orderData?.invoiceNo || 'INV-DEMO-2025',
        invoiceValue: '₹25,000',
        eWayBillNo: 'EWAY123456789012',
        privateMarka: 'DEMO-MARK-001',
        consignorCompanyName: orderData?.pickupCompanyName || 'Demo Sender Company Pvt Ltd',
        consignorGST: '29ABCDE1234F1Z5',
        consigneeCompanyName: orderData?.deliveryCompanyName || 'Demo Receiver Company Ltd',
        consigneeGST: '07XYZAB5678G2H9',
        fromCity: 'New Delhi',
        toCity: 'Mumbai',
        description: 'General Goods',
        noOfArticles: String(orderData?.noOfArticles || 5),
        actualWeight: '150',
        chargedWeight: '150',
        pricePerKg: '₹50',
        totalFreightCharges: '₹7,500',
        paymentBy: 'Consignor',
        deliveryType: 'Door to Door',
        copyType: 'Consignor',
        partnerGTA: 'DEMO-GTA-123',
        orderMode: 'PTL',
        // signatureImage removed - will be overlaid separately
      };

      // Step 1: Generate the base bilty PDF (without signature)
      const baseBiltyBlob = await generateBiltyPDF(sampleBiltyData, biltyPrintRef.current);

      // Step 2: Overlay the signature using pdf-lib with actual user IP
      const signedBiltyBlob = await overlaySignatureOnBilty(
        baseBiltyBlob,
        capturedSignature,
        'Demo User', // Optional signer name
        undefined,   // Optional signer phone
        userIpAddress, // Actual user IP address
        undefined // No geolocation
      );

      // Step 3: Create download link and trigger download
      const url = URL.createObjectURL(signedBiltyBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `POD-Signed-Demo-Bilty-${orderData?.lrNumber || 'SAMPLE'}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setDownloadingBilty(false);
    } catch (error) {
      console.error('Error generating demo bilty:', error);
      setDownloadingBilty(false);
      alert('Failed to generate demo bilty. Please try again.');
    }
  };

  // Calculate canvas size based on viewport and device pixel ratio
  useEffect(() => {
    const updateCanvasSize = () => {
      const width = Math.min(window.innerWidth - 32, 600); // Max 600px with 16px padding
      const height = window.innerWidth < 768 ? 180 : 200; // Increased height for mobile
      setCanvasSize({ width, height });
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Prevent scrolling while signing
  useEffect(() => {
    const canvas = signaturePadRef.current?.getCanvas();
    if (!canvas) return;

    const preventScroll = (e: TouchEvent) => {
      e.preventDefault();
    };

    canvas.addEventListener('touchstart', preventScroll, { passive: false });
    canvas.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', preventScroll);
      canvas.removeEventListener('touchmove', preventScroll);
    };
  }, [canvasSize]);

  const handleClear = () => {
    signaturePadRef.current?.clear();
    setIsEmpty(true);
  };

  const handleBegin = () => {
    setIsEmpty(false);
  };

  const handleSubmit = async () => {
    if (signaturePadRef.current && !isEmpty && isConfirmed && (token || isDemoMode)) {
      setSubmitting(true);
      setError('');

      try {
        // Get signature as base64
        const canvas = signaturePadRef.current.getCanvas();
        const base64Signature = canvas.toDataURL('image/png');
        
        // Store signature for demo bilty generation
        setCapturedSignature(base64Signature);
        
        // In demo mode, skip API call and directly proceed to success
        if (isDemoMode) {
          setSubmitted(true);
          setSubmitting(false);
          return;
        }
        
        // Prepare submission data
        const submissionData = {
          signatureToken: token,
          signatureData: base64Signature,
          signerPhone: '', // Can be added if needed
          signerName: '', // Can be added if needed
          userAgent: navigator.userAgent,
          ipAddress: userIpAddress, // Captured on mount from ipify - stored in DB, not shown on document
        };

        const base = (process.env.REACT_APP_BACKEND_API_ROUTE || '').replace(/\/$/, '');
        const baseTrimmed = base.replace(/\/$/, '');
        const url = `${baseTrimmed}/orders/submit-pod-signature`;

        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(submissionData),
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || `HTTP ${res.status}`);
        }

        const data = await res.json();

        if (data.success) {
          setSubmitted(true);
          setSubmitting(false);
        } else {
          throw new Error(data.message || 'Failed to submit signature');
        }
      } catch (err: any) {
        console.error('Error submitting signature:', err);
        setError(err.message || 'Failed to submit signature');
        setSubmitting(false);
      }
    }
  };

  const isSubmitDisabled = isEmpty || !isConfirmed || submitting;

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Verifying signature link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !orderData) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>✕</div>
            <h2 style={styles.errorTitle}>Unable to Verify</h2>
            <p style={styles.errorMessage}>{error}</p>
            <p style={styles.errorHint}>
              Please contact support if you believe this is an error.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state (after submission)
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.successTitle}>Signature Submitted Successfully!</h2>
            <p style={styles.successMessage}>
              Thank you for confirming the delivery of LRN: <strong>{orderData?.lrNumber}</strong>
            </p>
            <p style={styles.successHint}>
              {isDemoMode && downloadingBilty ? 'Generating demo bilty...' : 'You may close this page now.'}
            </p>
          </div>
        </div>
        {/* Hidden div for PDF rendering */}
        <div ref={biltyPrintRef} style={{ position: 'absolute', left: '-9999px', top: 0, width: 794, minHeight: 1123, overflow: 'hidden', backgroundColor: 'white' }}></div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Delivery Confirmation</h1>
          <p style={styles.message}>
            Please sign below to confirm receipt of goods.
          </p>
        </div>

        {/* Order Details */}
        {orderData && (
          <div style={styles.orderDetails}>
            <div style={{...styles.orderDetailRow, borderBottom: '1px solid #e5e7eb'}}>
              <span style={styles.orderDetailLabel}>LR Number:</span>
              <span style={styles.orderDetailValue}>{orderData.lrNumber}</span>
            </div>
            <div style={{...styles.orderDetailRow, borderBottom: '1px solid #e5e7eb'}}>
              <span style={styles.orderDetailLabel}>Invoice No:</span>
              <span style={styles.orderDetailValue}>{orderData.invoiceNo || 'N/A'}</span>
            </div>
            <div style={{...styles.orderDetailRow, borderBottom: '1px solid #e5e7eb'}}>
              <span style={styles.orderDetailLabel}>From:</span>
              <span style={styles.orderDetailValue}>{orderData.pickupCompanyName || 'N/A'}</span>
            </div>
            <div style={{...styles.orderDetailRow, borderBottom: '1px solid #e5e7eb'}}>
              <span style={styles.orderDetailLabel}>To:</span>
              <span style={styles.orderDetailValue}>{orderData.deliveryCompanyName || 'N/A'}</span>
            </div>
            <div style={{...styles.orderDetailRow, borderBottom: '1px solid #e5e7eb'}}>
              <span style={styles.orderDetailLabel}>No. of Articles:</span>
              <span style={styles.orderDetailValue}>{orderData.noOfArticles}</span>
            </div>
            <div style={{...styles.orderDetailRow, borderBottom: 'none'}}>
              <span style={styles.orderDetailLabel}>Order Date:</span>
              <span style={styles.orderDetailValue}>{formatDate(orderData.orderedAt)}</span>
            </div>
          </div>
        )}

        {/* Error message if submission failed */}
        {error && orderData && (
          <div style={styles.errorBanner}>
            <span style={styles.errorBannerText}>{error}</span>
          </div>
        )}

        {/* Signature Canvas */}
        <div style={styles.canvasWrapper}>
          <div style={styles.canvasContainer}>
            <SignatureCanvas
              ref={signaturePadRef}
              canvasProps={{
                width: canvasSize.width,
                height: canvasSize.height,
                style: styles.canvas,
              }}
              onBegin={handleBegin}
              backgroundColor="rgba(255, 255, 255, 1)"
              penColor="#000000"
              minWidth={1}
              maxWidth={3}
              dotSize={2}
              velocityFilterWeight={0.7}
            />
            {isEmpty && <div style={styles.signHereLabel}>Sign here</div>}
          </div>
        </div>

        {/* Checkbox */}
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={isConfirmed}
            onChange={(e) => setIsConfirmed(e.target.checked)}
            style={styles.checkbox}
            disabled={submitting}
          />
          <span style={styles.checkboxText}>
            I confirm that I have received the shipment.
          </span>
        </label>

        {/* Buttons */}
        <div style={styles.buttonContainer}>
          <button
            onClick={handleClear}
            style={{
              ...styles.button,
              ...styles.clearButton,
            }}
            type="button"
            disabled={submitting}
          >
            Clear
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            style={{
              ...styles.button,
              ...styles.submitButton,
              ...(isSubmitDisabled ? styles.disabledButton : {}),
            }}
            type="button"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Inline styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(to right, #f0f9ff, #e6f7ff)',
    padding: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    padding: '18px',
    maxWidth: '600px',
    width: '100%',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '12px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '4px',
    marginTop: 0,
  },
  message: {
    fontSize: '14px',
    color: '#4b5563',
    margin: 0,
  },
  orderDetails: {
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    padding: '14px',
    marginBottom: '12px',
    border: '1px solid #e5e7eb',
  },
  orderDetailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
  },
  orderDetailLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500',
  },
  orderDetailValue: {
    fontSize: '12px',
    color: '#1f2937',
    fontWeight: '600',
    textAlign: 'right',
    maxWidth: '60%',
    wordBreak: 'break-word',
  },
  canvasWrapper: {
    marginBottom: '12px',
  },
  canvasContainer: {
    position: 'relative',
    border: '2px dashed #cbd5e0',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    touchAction: 'none',
  },
  canvas: {
    display: 'block',
    touchAction: 'none',
    cursor: 'crosshair',
  },
  signHereLabel: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#e2e8f0',
    fontSize: '18px',
    fontWeight: 'bold',
    pointerEvents: 'none',
    userSelect: 'none',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '14px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#1f2937',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    marginTop: '2px',
    flexShrink: 0,
    accentColor: PRIMARY_COLOR,
  },
  checkboxText: {
    flex: 1,
    lineHeight: '1.5',
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    flexDirection: 'row',
  },
  button: {
    flex: 1,
    padding: '12px 20px',
    fontSize: '15px',
    fontWeight: '600',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  clearButton: {
    backgroundColor: 'transparent',
    color: PRIMARY_COLOR,
    border: `2px solid ${PRIMARY_COLOR}`,
  },
  submitButton: {
    backgroundColor: PRIMARY_COLOR,
    color: '#ffffff',
    boxShadow: `0 4px 12px rgba(52, 152, 219, 0.3)`,
  },
  disabledButton: {
    backgroundColor: '#cbd5e0',
    color: '#a0aec0',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: `4px solid #e5e7eb`,
    borderTopColor: PRIMARY_COLOR,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 20px',
  },
  loadingText: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  errorIcon: {
    width: '56px',
    height: '56px',
    backgroundColor: ERROR_COLOR,
    color: 'white',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '12px',
  },
  errorMessage: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '8px',
  },
  errorHint: {
    fontSize: '14px',
    color: '#9ca3af',
    marginTop: '16px',
  },
  errorBanner: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '20px',
  },
  errorBannerText: {
    fontSize: '14px',
    color: '#991b1b',
    display: 'block',
  },
  successContainer: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  successIcon: {
    width: '56px',
    height: '56px',
    backgroundColor: SUCCESS_COLOR,
    color: 'white',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '12px',
  },
  successMessage: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '8px',
  },
  successHint: {
    fontSize: '14px',
    color: '#9ca3af',
    marginTop: '16px',
  },
};

// Add global styles for spinner animation
const styleTag = document.createElement('style');
styleTag.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
if (!document.querySelector('style[data-signature-styles]')) {
  styleTag.setAttribute('data-signature-styles', 'true');
  document.head.appendChild(styleTag);
}

export default DeliverySignature;
